import {
  Attributes,
  type ListOperator,
  OPERATORS,
  Restrictions,
  type RnbCalculatorPayload,
  SellerOperator,
} from 'typings'
import { ApplyTo } from 'ui/components/PromotionForm/Restrictions/typings'

export function mapUsage(data: RnbCalculatorPayload): Restrictions['maxUsage'] {
  return data.maxUsage || undefined
}

export function mapMaxUsagePerClient(
  data: RnbCalculatorPayload
): Restrictions['maxUsagePerClient'] {
  return data.maxUsagePerClient || undefined
}

export function mapMaxItemsPerCart(
  data: RnbCalculatorPayload
): Restrictions['maxItemsPerCart'] {
  const { maxNumberOfAffectedItems, isAppliedToMostExpensive } = data

  if (maxNumberOfAffectedItems === 0) return undefined

  if (isAppliedToMostExpensive === undefined)
    return {
      value: maxNumberOfAffectedItems,
    }

  return {
    value: maxNumberOfAffectedItems,
    applyTo: isAppliedToMostExpensive
      ? ApplyTo.mostExpensive
      : ApplyTo.cheapest,
  }
}

export function mapAccumulate(
  data: RnbCalculatorPayload
): Restrictions['accumulate'] {
  return Boolean(data.cumulative)
}

export function mapAccumulateWithManualPrices(
  data: RnbCalculatorPayload
): Restrictions['accumulateWithManualPrices'] {
  return Boolean(data.accumulateWithManualPrice)
}

export function mapSellerIds(
  data: RnbCalculatorPayload
): Restrictions['sellers'] {
  const { idSeller, idSellerIsInclusive, optIn } = data

  const processSellers = (sellers) =>
    sellers.map((id) => ({ id: id.trim(), name: '' }))

  if (optIn?.sellers)
    return {
      items: processSellers(optIn.sellers).reverse(),
      operator: SellerOperator.OPTIN,
    }

  if (!idSeller || !idSeller.length) return undefined

  const items = processSellers(idSeller.split(','))
  const operator: SellerOperator = idSellerIsInclusive
    ? SellerOperator.EQUALS
    : SellerOperator.DIFFERENT

  return { items, operator }
}

export function mapSalesChannels(
  data: RnbCalculatorPayload
): Restrictions['salesChannels'] {
  const { idsSalesChannel, areSalesChannelIdsExclusive } = data

  if (!idsSalesChannel || !idsSalesChannel.length) return undefined

  const items = idsSalesChannel.map((id) => ({ id: id.trim(), name: '' }))
  const operator: ListOperator = areSalesChannelIdsExclusive
    ? OPERATORS.DIFFERENT
    : OPERATORS.EQUALS

  return { items, operator }
}

export function mapMaxItemsPerAttribute(
  data: RnbCalculatorPayload
): Restrictions['maxItemsPerAttribute'] {
  const {
    maxNumberOfAffectedItems,
    maxNumberOfAffectedItemsGroupKey,
    isAppliedToMostExpensive,
  } = data

  if (maxNumberOfAffectedItems === 0) return undefined

  const attributeMap = {
    perProductId: Attributes.PRODUCT,
    perSku: Attributes.SKU,
    perCart: Attributes.CART,
  }

  return {
    attribute: attributeMap[maxNumberOfAffectedItemsGroupKey],
    value: maxNumberOfAffectedItems,
    applyTo: isAppliedToMostExpensive
      ? ApplyTo.mostExpensive
      : ApplyTo.cheapest,
  }
}

export function mapBudget(data: RnbCalculatorPayload): Restrictions['budget'] {
  if (!data.budget) return undefined
  const {
    budget: { budgetId, allocationId },
  } = data
  return {
    id: budgetId,
    allocationId,
  }
}

export function mapRestrictions(data: RnbCalculatorPayload): Restrictions {
  return {
    maxUsage: mapUsage(data),
    maxUsagePerClient: mapMaxUsagePerClient(data),
    maxItemsPerCart: mapMaxItemsPerCart(data),
    maxItemsPerAttribute: mapMaxItemsPerAttribute(data),
    accumulate: mapAccumulate(data),
    accumulateWithManualPrices: mapAccumulateWithManualPrices(data),
    budget: mapBudget(data),
    sellers: mapSellerIds(data),
    salesChannels: mapSalesChannels(data),
    origin: data.origin,
  }
}
